/**
 * Session Timeout Utilities
 * Manages session timeout tracking for idle-based logout
 */

const SESSION_START_TIME_KEY = 'usls_session_start_time';
const SESSION_TIMEOUT_MS = 45 * 60 * 1000; // 45 minutes
export const WARNING_TIME_MS = 5 * 60 * 1000; // 5 minutes warning before expiration

/**
 * Set the session start time to current timestamp
 */
export function setSessionStartTime(): void {
  try {
    localStorage.setItem(SESSION_START_TIME_KEY, Date.now().toString());
  } catch (error) {
    console.error('[SessionTimeout] Error setting session start time:', error);
  }
}

/**
 * Get the session start time
 * @returns timestamp in milliseconds, or null if not set
 */
export function getSessionStartTime(): number | null {
  try {
    const startTime = localStorage.getItem(SESSION_START_TIME_KEY);
    return startTime ? parseInt(startTime, 10) : null;
  } catch (error) {
    console.error('[SessionTimeout] Error getting session start time:', error);
    return null;
  }
}

/**
 * Clear the session start time
 */
export function clearSessionStartTime(): void {
  try {
    localStorage.removeItem(SESSION_START_TIME_KEY);
  } catch (error) {
    console.error('[SessionTimeout] Error clearing session start time:', error);
  }
}

/**
 * Check if the session has expired (45 minutes since start)
 * @returns true if session has expired, false otherwise
 */
export function isSessionExpired(): boolean {
  const startTime = getSessionStartTime();

  if (!startTime) {
    // No session start time means session hasn't started yet, so it's not expired
    // The caller should set the session start time if needed
    return false;
  }

  const elapsed = Date.now() - startTime;
  return elapsed > SESSION_TIMEOUT_MS;
}

/**
 * Get the time remaining in the session (in milliseconds)
 * @returns milliseconds remaining, or 0 if expired
 */
export function getSessionTimeRemaining(): number {
  const startTime = getSessionStartTime();

  if (!startTime) {
    return 0;
  }

  const elapsed = Date.now() - startTime;
  const remaining = SESSION_TIMEOUT_MS - elapsed;

  return remaining > 0 ? remaining : 0;
}

/**
 * Update the session start time to now (reset the timer)
 * This can be used for "activity-based" timeout where activity resets the timer
 */
export function resetSessionTimeout(): void {
  setSessionStartTime();
}

/**
 * Check if we should show the session warning (5 minutes or less remaining)
 * @returns true if warning should be shown
 */
export function shouldShowSessionWarning(): boolean {
  const remaining = getSessionTimeRemaining();
  return remaining > 0 && remaining <= WARNING_TIME_MS;
}
